import { useState, useRef, useEffect, useCallback } from 'react';
import { Shape } from '../types/Shape';
import { Annotation } from '../types/Annotation';
import { updateArrowPosition } from '../utils/arrowUtils';
import { handleShapeUpdate as shapeUpdateHelper } from '../utils/shapeHelpers';
import { exportData, importData } from '../utils/fileUtils';
import { useHistory } from './useHistory';
import { useToast } from '../contexts/ToastContext';
import { ShapeData } from '../types/Collaboration';
import { deserializeShapes, applyRemoteChanges } from '../utils/syncUtils';

/**
 * Collaboration configuration for useCanvasState
 */
export interface CollaborationConfig {
  /**
   * Whether collaboration mode is enabled
   */
  enabled: boolean;

  /**
   * Callback when shapes change (for sync to other participants)
   */
  onShapesChange?: (shapes: Shape[]) => void;
}

export interface UseCanvasStateReturn {
  // Shapes state
  shapes: Shape[];
  setShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  shapesRef: React.MutableRefObject<Shape[]>;

  // Unsaved changes
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;

  // History instance
  history: ReturnType<typeof useHistory>;

  // Shape operations
  updateConnectedArrows: (shapeId: string, currentShapes?: Shape[]) => void;
  handleSave: () => void;
  handleLoad: (file: File) => Promise<void>;
  handleObjectUpdate: (updates: Partial<Shape>, selectedShapes: string[]) => void;
  handleShapeUpdate: (shapeId: string, updates: Partial<Shape> | { action: string; [key: string]: any }) => void;
  handleExtractPage: (shapeId: string, pageNum: number) => void;
  handleAnnotationUpdate: (shapeId: string, annotations: Annotation[]) => void;

  // Collaboration support
  applyRemoteShapes: (shapes: ShapeData[]) => void;
  applyRemoteUpdate: (action: string, data: { shapes?: ShapeData[]; shapeId?: string; shapeIds?: string[] }) => void;
  setCollaborationConfig: (config: Partial<CollaborationConfig>) => void;
  isCollaborating: boolean;
}

export function useCanvasState(): UseCanvasStateReturn {
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const shapesRef = useRef<Shape[]>([]);
  const history = useHistory();
  const toast = useToast();

  // Collaboration state
  const [collaborationConfig, setCollaborationConfigState] = useState<CollaborationConfig>({
    enabled: false,
  });
  const collaborationConfigRef = useRef<CollaborationConfig>({ enabled: false });
  const isApplyingRemoteRef = useRef(false); // Flag to prevent sync loops

  // Sync shapesRef with shapes state and notify collaboration
  useEffect(() => {
    shapesRef.current = shapes;

    // Notify collaboration callback if enabled and not applying remote changes
    if (
      collaborationConfigRef.current.enabled &&
      collaborationConfigRef.current.onShapesChange &&
      !isApplyingRemoteRef.current
    ) {
      collaborationConfigRef.current.onShapesChange(shapes);
    }
  }, [shapes]);

  // Page unload warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Initialize arrow properties and order on mount
  useEffect(() => {
    setShapes(prev =>
      prev.map(shape => {
        const shapeWithOrder = {
          ...shape,
          order: shape.order ?? 2
        };
        if (shape.type === 'arrow') {
          return {
            ...shapeWithOrder,
            startArrow: shape.startArrow ?? false,
            endArrow: shape.endArrow ?? true,
          };
        }
        return shapeWithOrder;
      })
    );
  }, []);

  // Update connected arrows when a shape moves
  const updateConnectedArrows = useCallback((shapeId: string, currentShapes?: Shape[]) => {
    const shapesToUpdate = currentShapes || shapes;

    setShapes(prev => {
      const updatedShapes = [...prev];

      const shape = updatedShapes.find(s => s.id === shapeId);
      if (shape && shape.connectedArrows) {
        shape.connectedArrows.forEach(arrowId => {
          const arrowIndex = updatedShapes.findIndex(s => s.id === arrowId);
          if (arrowIndex !== -1) {
            const arrow = updatedShapes[arrowIndex];
            if (arrow && arrow.type === 'arrow') {
              const isActuallyConnected =
                arrow.startShapeId === shapeId || arrow.endShapeId === shapeId;

              if (isActuallyConnected) {
                const currentShape = shapesToUpdate.find(s => s.id === shapeId);
                if (currentShape) {
                  const updatedArrow = updateArrowPosition(arrow, currentShape, shapeId);
                  updatedShapes[arrowIndex] = updatedArrow;
                }
              }
            }
          }
        });
      }

      return updatedShapes;
    });
  }, [shapes]);

  // Save to file
  const handleSave = useCallback(async () => {
    try {
      await exportData(shapes);
      setHasUnsavedChanges(false);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }, [shapes, toast]);

  // Load from file
  const handleLoad = useCallback(async (file: File) => {
    try {
      const loadedShapes = await importData(file);
      if (loadedShapes) {
        setShapes(loadedShapes);
        history.pushHistory(loadedShapes);
        // Fix order values
        setShapes(prev =>
          prev.map(shape => {
            const shapeWithOrder = {
              ...shape,
              order: shape.order ?? 2
            };
            if (shape.type === 'arrow') {
              return {
                ...shapeWithOrder,
                startArrow: shape.startArrow ?? false,
                endArrow: shape.endArrow ?? true,
              };
            }
            return shapeWithOrder;
          })
        );
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Load failed:', error);
      toast.error('파일을 불러오는 데 실패했습니다.');
    }
  }, [history]);

  // Update selected shapes
  const handleObjectUpdate = useCallback((updates: Partial<Shape>, selectedShapes: string[]) => {
    if (selectedShapes.length > 0) {
      setShapes(prev =>
        prev.map(shape =>
          selectedShapes.includes(shape.id)
            ? { ...shape, ...updates }
            : shape
        )
      );
      setHasUnsavedChanges(true);
    }
  }, []);

  // Update single shape
  const handleShapeUpdate = useCallback((shapeId: string, updates: Partial<Shape> | { action: string; [key: string]: any }) => {
    shapeUpdateHelper(shapeId, updates, setShapes);
    setHasUnsavedChanges(true);
  }, []);

  // Extract PDF page as new shape
  const handleExtractPage = useCallback((shapeId: string, pageNum: number) => {
    setShapes(prev => {
      const sourceShape = prev.find(s => s.id === shapeId);
      if (!sourceShape || !sourceShape.pdfFile) return prev;

      const maxOrder = Math.max(...prev.map(s => s.order ?? 0), 0);
      const newOrder = maxOrder % 2 === 0 ? maxOrder + 2 : maxOrder + 1;

      const newShape: Shape = {
        ...sourceShape,
        id: Date.now().toString(),
        x: (sourceShape.x || 0) + 50,
        y: (sourceShape.y || 0) + 50,
        order: newOrder,
        currentPage: pageNum,
        totalPages: sourceShape.totalPages,
        lockPageNavigation: true,
        connectedArrows: [],
      };

      return [...prev, newShape];
    });
    setHasUnsavedChanges(true);
  }, []);

  // Update annotations for a shape (image annotation feature)
  const handleAnnotationUpdate = useCallback((shapeId: string, annotations: Annotation[]) => {
    setShapes(prev =>
      prev.map(shape =>
        shape.id === shapeId
          ? { ...shape, annotations }
          : shape
      )
    );
    setHasUnsavedChanges(true);
  }, []);

  // ============================================================
  // Collaboration Support
  // ============================================================

  /**
   * Set collaboration configuration
   */
  const setCollaborationConfig = useCallback((config: Partial<CollaborationConfig>) => {
    setCollaborationConfigState(prev => {
      const newConfig = { ...prev, ...config };
      collaborationConfigRef.current = newConfig;
      return newConfig;
    });
  }, []);

  /**
   * Apply remote shapes (full state sync)
   * Used when joining a session or for recovery
   */
  const applyRemoteShapes = useCallback((remoteShapes: ShapeData[]) => {
    // Set flag to prevent sync loop
    isApplyingRemoteRef.current = true;

    try {
      const shapes = deserializeShapes(remoteShapes);

      // Apply order and arrow defaults like local shapes
      const processedShapes = shapes.map(shape => {
        const shapeWithOrder = {
          ...shape,
          order: shape.order ?? 2,
        };

        if (shape.type === 'arrow') {
          return {
            ...shapeWithOrder,
            startArrow: shape.startArrow ?? false,
            endArrow: shape.endArrow ?? true,
          };
        }

        return shapeWithOrder;
      });

      setShapes(processedShapes);

      // Push to history for undo/redo support
      history.pushHistory(processedShapes);
    } finally {
      // Reset flag after a small delay to allow state to settle
      setTimeout(() => {
        isApplyingRemoteRef.current = false;
      }, 0);
    }
  }, [history]);

  /**
   * Apply a remote update (incremental)
   * Used for real-time sync of individual changes
   */
  const applyRemoteUpdate = useCallback((
    action: string,
    data: { shapes?: ShapeData[]; shapeId?: string; shapeIds?: string[] }
  ) => {
    // Set flag to prevent sync loop
    isApplyingRemoteRef.current = true;

    try {
      setShapes(prev => {
        const updatedShapes = applyRemoteChanges(
          prev,
          action as 'add' | 'update' | 'delete' | 'bulk',
          data
        );

        // Process new shapes with defaults
        return updatedShapes.map(shape => {
          const shapeWithOrder = {
            ...shape,
            order: shape.order ?? 2,
          };

          if (shape.type === 'arrow') {
            return {
              ...shapeWithOrder,
              startArrow: shape.startArrow ?? false,
              endArrow: shape.endArrow ?? true,
            };
          }

          return shapeWithOrder;
        });
      });
    } finally {
      // Reset flag after a small delay to allow state to settle
      setTimeout(() => {
        isApplyingRemoteRef.current = false;
      }, 0);
    }
  }, []);

  /**
   * Check if currently in collaboration mode
   */
  const isCollaborating = collaborationConfig.enabled;

  return {
    shapes,
    setShapes,
    shapesRef,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    history,
    updateConnectedArrows,
    handleSave,
    handleLoad,
    handleObjectUpdate,
    handleShapeUpdate,
    handleExtractPage,
    handleAnnotationUpdate,

    // Collaboration support
    applyRemoteShapes,
    applyRemoteUpdate,
    setCollaborationConfig,
    isCollaborating,
  };
}
